import {
    CROSS_BRANCH_DEPENDENCIES,
    FLOW_STATES,
    VERIFICATION_BRANCHES,
} from './flowDefinition';

const SUBMISSION = {
    bidProcessing: 1100,
};

const CONVERGENCE = {
    analysisProcessing: 2400,
    decisionProcessing: 1600,
    decisionTravel: 1400,
    tail: 1200,
};

const DEPENDENCY_TRAVEL = 1800;

/**
 * Branch-level timing. Stages do not inherit a sequence from this object.
 */
const branchTimings = {
    'document-forensics': {
        startDelay: 420,
        approachTravel: 2600,
        handoffTravel: 580,
        analysisTravel: 1600,
    },
    'company-intelligence': {
        startDelay: 160,
        approachTravel: 2100,
        handoffTravel: 520,
        analysisTravel: 1400,
    },
    'cyber-security': {
        startDelay: 780,
        approachTravel: 3200,
        handoffTravel: 640,
        analysisTravel: 1800,
    },
    'human-identity': {
        startDelay: 280,
        approachTravel: 2400,
        handoffTravel: 500,
        analysisTravel: 1500,
    },
};

/**
 * Per-stage timing. Stages of the same branch start independently unless they
 * appear in CROSS_BRANCH_DEPENDENCIES as a wait target.
 */
const stageTimings = {
    'document-forensics-stage-1': { startDelay: 80, travel: 1400, processing: 2500, resultTravel: 900 },
    'document-forensics-stage-2': { startDelay: 360, travel: 1700, processing: 4200, resultTravel: 1100 },
    'document-forensics-stage-3': { startDelay: 200, travel: 1500, processing: 3000, resultTravel: 1000 },
    'company-intelligence-stage-1': { startDelay: 60, travel: 1200, processing: 2200, resultTravel: 800 },
    'company-intelligence-stage-2': { startDelay: 280, travel: 1500, processing: 3600, resultTravel: 1000 },
    'company-intelligence-stage-3': { startDelay: 140, travel: 1300, processing: 2800, resultTravel: 900 },
    'company-intelligence-stage-4': { startDelay: 400, travel: 1600, processing: 3400, resultTravel: 1100 },
    'company-intelligence-stage-5': { startDelay: 500, travel: 1800, processing: 4100, resultTravel: 1200 },
    'cyber-security-stage-1': { startDelay: 100, travel: 1800, processing: 3800, resultTravel: 1100 },
    'cyber-security-stage-2': { startDelay: 340, travel: 2100, processing: 4600, resultTravel: 1300 },
    'cyber-security-stage-3': { startDelay: 220, travel: 1900, processing: 3200, resultTravel: 1200 },
    'cyber-security-stage-4': { startDelay: 400, travel: 1600, processing: 3400, resultTravel: 1100 },
    'cyber-security-stage-5': { startDelay: 500, travel: 1800, processing: 4100, resultTravel: 1200 },
    'human-identity-stage-1': { startDelay: 90, travel: 1300, processing: 2400, resultTravel: 850 },
    'human-identity-stage-2': { startDelay: 260, travel: 1600, processing: 3100, resultTravel: 1000 },
    'human-identity-stage-3': { startDelay: 180, travel: 1450, processing: 2700, resultTravel: 950 },
};

const defaultStageTiming = {
    startDelay: 200,
    travel: 1500,
    processing: 3000,
    resultTravel: 1000,
};

/**
 * Checks that end with something other than a clean pass, keyed by bid outcome.
 * A failed check does not stop sibling stages unless they depend on it.
 */
const stageResultsByOutcome = {
    Rejected: {
        'document-forensics-stage-3': FLOW_STATES.warning,
        'company-intelligence-stage-5': FLOW_STATES.failed,
    },
    'Action Required': {
        'document-forensics-stage-3': FLOW_STATES.warning,
        'human-identity-stage-3': FLOW_STATES.warning,
    },
    Verifying: {
        'human-identity-stage-2': FLOW_STATES.warning,
    },
};

const getWorstState = (states) => {
    if (states.includes(FLOW_STATES.failed)) return FLOW_STATES.failed;
    if (states.includes(FLOW_STATES.warning)) return FLOW_STATES.warning;

    return FLOW_STATES.completed;
};

const getEdgeId = (source, target) => `${source}--${target}`;

const getStageId = (branchId, stageIndex) => `${branchId}-stage-${stageIndex + 1}`;

const getIncomingStageDependencies = (stageId) => (
    CROSS_BRANCH_DEPENDENCIES.filter((dependency) => (
        dependency.waitTarget === stageId || dependency.target === stageId
    ))
);

const getWorstKnownResult = (stageResults, stageId) => (
    stageResults[stageId] || FLOW_STATES.completed
);

/**
 * Compiles the scenario into an ordered timeline. Stages of a branch start in
 * parallel. A stage waits only when CROSS_BRANCH_DEPENDENCIES names it as a
 * wait target.
 */
export const createVerificationTimeline = (bid) => {
    const stageResults = stageResultsByOutcome[bid.outcome.status] || {};
    const dependencyBySource = CROSS_BRANCH_DEPENDENCIES.reduce((dependencies, dependency) => ({
        ...dependencies,
        [dependency.source]: dependency,
    }), {});

    const stateEvents = [];
    const signals = [];
    const stageCompletedAt = {};
    const branchStates = [];
    const analysisInputsAt = [];

    const addState = (at, nodeId, state) => {
        stateEvents.push({ at, nodeId, state });
    };

    const addSignal = ({
        at,
        duration,
        result = FLOW_STATES.completed,
        settleAt,
        source,
        target,
    }) => {
        const signal = {
            id: `${getEdgeId(source, target)}@${Math.round(at)}`,
            edgeId: getEdgeId(source, target),
            startAt: at,
            endAt: at + duration,
            duration,
            result,
            settleAt: settleAt ?? (at + duration),
        };

        signals.push(signal);

        return signal;
    };

    addState(180, 'bid', FLOW_STATES.processing);

    const submittedAt = 180 + SUBMISSION.bidProcessing;

    addState(submittedAt, 'bid', FLOW_STATES.completed);

    const scheduledStages = [];

    VERIFICATION_BRANCHES.forEach((branch) => {
        const timing = branchTimings[branch.id];
        const statusId = `${branch.id}-status`;
        const outputId = `${branch.id}-output`;
        const branchStartedAt = submittedAt + timing.startDelay;
        const categoryReachedAt = branchStartedAt + timing.approachTravel;

        const approachSignal = addSignal({
            at: branchStartedAt,
            duration: timing.approachTravel,
            source: 'bid',
            target: branch.id,
        });

        addState(categoryReachedAt, branch.id, FLOW_STATES.processing);

        const handoffSignal = addSignal({
            at: categoryReachedAt,
            duration: timing.handoffTravel,
            source: branch.id,
            target: statusId,
        });

        const stagesReadyAt = categoryReachedAt + timing.handoffTravel;

        branch.stages.forEach((_stageLabel, stageIndex) => {
            const stageId = getStageId(branch.id, stageIndex);
            const stageTiming = stageTimings[stageId] || defaultStageTiming;

            scheduledStages.push({
                approachSignal,
                branchId: branch.id,
                handoffSignal,
                outputId,
                readyAt: stagesReadyAt + stageTiming.startDelay,
                result: getWorstKnownResult(stageResults, stageId),
                stageId,
                stageTiming,
                statusId,
            });
        });
    });

    // Resolve explicit waits after every independent start time is known.
    const resolvedStarts = {};

    const resolveStart = (stageId, visiting = []) => {
        if (resolvedStarts[stageId] !== undefined) return resolvedStarts[stageId];
        if (visiting.includes(stageId)) return 0;

        const scheduled = scheduledStages.find((stage) => stage.stageId === stageId);
        const incoming = getIncomingStageDependencies(stageId);
        const readyAt = scheduled?.readyAt ?? 0;

        if (!incoming.length) {
            resolvedStarts[stageId] = readyAt;

            return readyAt;
        }

        const startsAt = incoming.reduce((latest, dependency) => {
            const sourceDoneAt = resolveStart(dependency.source, [...visiting, stageId])
                + (stageTimings[dependency.source] || defaultStageTiming).travel
                + (stageTimings[dependency.source] || defaultStageTiming).processing
                + DEPENDENCY_TRAVEL;

            return Math.max(latest, sourceDoneAt);
        }, readyAt);

        resolvedStarts[stageId] = startsAt;

        return startsAt;
    };

    scheduledStages.forEach((stage) => resolveStart(stage.stageId));

    const branchCompletion = {};

    scheduledStages.forEach((stage) => {
        const startsAt = resolvedStarts[stage.stageId];
        const processingAt = startsAt + stage.stageTiming.travel;
        const completedAt = processingAt + stage.stageTiming.processing;
        const result = stage.result;

        addSignal({
            at: startsAt,
            duration: stage.stageTiming.travel,
            result,
            settleAt: completedAt,
            source: stage.statusId,
            target: stage.stageId,
        });
        addState(processingAt, stage.stageId, FLOW_STATES.processing);
        addState(completedAt, stage.stageId, result);

        stageCompletedAt[stage.stageId] = completedAt;

        const outgoingDependency = dependencyBySource[stage.stageId];

        if (outgoingDependency && result !== FLOW_STATES.failed) {
            addSignal({
                at: completedAt,
                duration: DEPENDENCY_TRAVEL,
                result,
                settleAt: completedAt + DEPENDENCY_TRAVEL,
                source: stage.stageId,
                target: outgoingDependency.target,
            });
        }

        addSignal({
            at: completedAt,
            duration: stage.stageTiming.resultTravel,
            result,
            settleAt: completedAt + stage.stageTiming.resultTravel,
            source: stage.stageId,
            target: stage.outputId,
        });

        const finishedAt = completedAt + stage.stageTiming.resultTravel;
        const current = branchCompletion[stage.branchId] || {
            approachSignal: stage.approachSignal,
            executedStates: [],
            handoffSignal: stage.handoffSignal,
            lastResultAt: finishedAt,
            outputId: stage.outputId,
            statusId: stage.statusId,
        };

        current.executedStates.push(result);
        current.lastResultAt = Math.max(current.lastResultAt, finishedAt);
        branchCompletion[stage.branchId] = current;
    });

    VERIFICATION_BRANCHES.forEach((branch) => {
        const completion = branchCompletion[branch.id];
        const timing = branchTimings[branch.id];
        const branchState = getWorstState(completion.executedStates);

        completion.approachSignal.result = branchState;
        completion.approachSignal.settleAt = completion.lastResultAt;
        completion.handoffSignal.result = branchState;
        completion.handoffSignal.settleAt = completion.lastResultAt;

        addState(completion.lastResultAt, branch.id, branchState);
        addState(completion.lastResultAt, completion.statusId, branchState);
        addState(completion.lastResultAt, completion.outputId, branchState);
        addSignal({
            at: completion.lastResultAt,
            duration: timing.analysisTravel,
            result: branchState,
            settleAt: completion.lastResultAt + timing.analysisTravel,
            source: completion.outputId,
            target: 'central-analysis',
        });

        analysisInputsAt.push(completion.lastResultAt + timing.analysisTravel);
        branchStates.push(branchState);
    });

    const analysisStartedAt = Math.min(...analysisInputsAt);
    const analysisCompletedAt = Math.max(...analysisInputsAt) + CONVERGENCE.analysisProcessing;
    const verdict = getWorstState(branchStates);

    addState(analysisStartedAt, 'central-analysis', FLOW_STATES.processing);
    addState(analysisCompletedAt, 'central-analysis', verdict);

    const decisionStartedAt = analysisCompletedAt + CONVERGENCE.decisionTravel;
    const decidedAt = decisionStartedAt + CONVERGENCE.decisionProcessing;

    addSignal({
        at: analysisCompletedAt,
        duration: CONVERGENCE.decisionTravel,
        result: verdict,
        settleAt: decidedAt,
        source: 'central-analysis',
        target: 'final-decision',
    });

    addState(decisionStartedAt, 'final-decision', FLOW_STATES.processing);
    addState(decidedAt, 'final-decision', verdict);

    return {
        duration: decidedAt + CONVERGENCE.tail,
        signals: signals.sort((first, second) => first.startAt - second.startAt),
        stateEvents: stateEvents.sort((first, second) => first.at - second.at),
        verdict,
    };
};

export default createVerificationTimeline;
